import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TrendingUp, Calendar, Eye, ArrowRight, Share2 } from 'lucide-react';
import type { NewsArticleWithRelations } from '../../../types/news';
import { useNewsHandlers } from './NewsHandlers';

interface TrendingArticlesProps {
  articles: NewsArticleWithRelations[];
}

const TrendingArticles: React.FC<TrendingArticlesProps> = ({ articles }) => {
  if (articles.length === 0) return null;
  
  const { handleShare } = useNewsHandlers();
  
  // Take the top 4 articles for the trending section
  const trendingArticles = articles.slice(0, 4);
  
  return (
    <div className="mb-16">
      <motion.div 
        className="flex items-center gap-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <TrendingUp className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-bold">Tendências</h2>
      </motion.div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {trendingArticles.map((article, index) => (
          <motion.div
            key={article.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            viewport={{ once: true }}
            whileHover={{ y: -8 }}
            className="group relative bg-card/20 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/10 transition-all duration-300"
          >
            <Link to={`/news/${article.slug}`} className="block">
              <div className="aspect-[4/3] relative overflow-hidden">
                <motion.img
                  src={article.cover_image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                
                {/* Trending rank */}
                <motion.div 
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-primary/80 flex items-center justify-center text-primary-foreground font-bold text-sm"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 + index * 0.1, type: "spring" }}
                  whileHover={{ scale: 1.1 }}
                >
                  {index + 1}
                </motion.div>
              </div>
              
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(article.published_at || '').toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    {article.views_count || 0}
                  </span>
                </div>
                
                <div className="mt-4 flex justify-between items-center">
                  <motion.button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleShare(article);
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </motion.button>
                  
                  <motion.span 
                    className="inline-flex items-center text-primary text-sm font-medium group-hover:translate-x-1 transition-transform"
                    whileHover={{ x: 3 }}
                  >
                    Ler mais <ArrowRight className="ml-1 w-4 h-4" />
                  </motion.span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default TrendingArticles;